import * as React from "react";
import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Modal as MuiModal,
  TextField,
  Radio,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { normalizeWhitespaceForSave } from "../utils/WhitespaceHelper";
import { useFlash } from "../FlashContext";

interface Props {
  addAllergyModalOpen: boolean;
  closeAddAllergyModal: () => void;
  patientId: string;
  selectedAllergy: any;
  onSuccess: () => void;
}

// constants
const ALLERGEN_MAX_LENGTH = 100;
const REACTION_MAX_LENGTH = 500;

const AddAllergy: React.FC<Props> = ({
  addAllergyModalOpen,
  closeAddAllergyModal,
  patientId,
  selectedAllergy,
  onSuccess,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();
  const [allergen, setAllergen] = React.useState(
    selectedAllergy?.allergen ?? ""
  );
  const [reaction, setReaction] = React.useState(
    selectedAllergy?.reaction ?? ""
  );
  const [reactionType, setReactionType] = React.useState(
    selectedAllergy?.reaction_type ?? "allergy"
  );
  const [severity, setSeverity] = React.useState(
    selectedAllergy?.severity ?? ""
  );
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async () => {
    if (!allergen) {
      showToast({
        title: "Allergen Required",
        text: "Please enter Allergen",
        type: "error",
      });
      return;
    }

    if (!reaction) {
      showToast({
        title: "Reaction Description Required",
        text: "Please enter Description of Reaction",
        type: "error",
      });
      return;
    }

    setLoading(true);

    const allergy = {
      allergen: normalizeWhitespaceForSave(allergen),
      reaction: normalizeWhitespaceForSave(reaction),
      patient_id: patientId,
      reaction_type: reactionType,
      severity: severity || null,
    };

    const isEdit = !!selectedAllergy;
    const url = isEdit ? `/allergies/${selectedAllergy.id}` : "/allergies";
    const method = isEdit ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({ allergy }),
      });

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "Allergy Saved",
          text: `Allergy ${isEdit ? "updated" : "created"} successfully`,
          type: "success",
        });

        onSuccess();
        closeAddAllergyModal();
      } else {
        showToast({
          title: "Error Saving Allergy",
          text: result.message,
          type: "error",
        });
      }
    } catch (error: any) {
      showToast({
        title: "Error Saving Allergy",
        text: error?.message ?? "Failed to save allergy",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MuiModal
      open={addAllergyModalOpen}
      className="modal-primary"
      onClose={closeAddAllergyModal}
    >
      <div className="paper" style={{ width: 430 }}>
        <div className="paperInner">
          <Grid container>
            <Grid item xs={12}>
              <div className="main-header">
                <span>{selectedAllergy ? "Edit Allergy" : "Add Allergy"}</span>
                <span id="dismiss-button" onClick={closeAddAllergyModal}>
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "24px",
                paddingRight: "24px",
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="allergen" className="field-label">
                    Allergen*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="allergen"
                    size="small"
                    value={allergen}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="Allergen Name"
                    error={allergen.length > ALLERGEN_MAX_LENGTH}
                    helperText={
                      allergen.length > ALLERGEN_MAX_LENGTH
                        ? `Maximum length is ${ALLERGEN_MAX_LENGTH} characters.`
                        : ""
                    }
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      const { value } = event.target;
                      if (value.length > ALLERGEN_MAX_LENGTH) return;
                      setAllergen(value);
                    }}
                    inputProps={{ maxLength: ALLERGEN_MAX_LENGTH }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="reactionType" className="field-label">
                    Type*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={reactionType === "allergy"}
                      onChange={(event) => {
                        setReactionType(event.target.value);
                      }}
                      value="allergy"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Allergy" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Allergy</p>
                    <Radio
                      checked={reactionType === "adverse_reaction"}
                      onChange={(event) => {
                        setReactionType(event.target.value);
                      }}
                      value="adverse_reaction"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Adverse Reaction" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 2, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Adverse Reaction</p>
                  </div>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="reaction" className="field-label">
                    Description of Reaction*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="reaction"
                    size="small"
                    value={reaction}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="Reaction"
                    error={reaction.length > REACTION_MAX_LENGTH}
                    helperText={
                      reaction.length > REACTION_MAX_LENGTH
                        ? `Maximum length is ${REACTION_MAX_LENGTH} characters.`
                        : ""
                    }
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      const { value } = event.target;
                      if (value.length > REACTION_MAX_LENGTH) return;
                      setReaction(value);
                    }}
                    inputProps={{ maxLength: REACTION_MAX_LENGTH }}
                    multiline
                    minRows={3}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                      "& .MuiOutlinedInput-root": {
                        padding: "0px !important",
                      },
                      "& .MuiInputBase-inputMultiline": {
                        padding: "10.5px !important",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="severity" className="field-label">
                    Severity*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={severity === "mild"}
                      onChange={(event) => {
                        setSeverity(event.target.value);
                      }}
                      value="mild"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Mild" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Mild</p>
                    <Radio
                      checked={severity === "intermediate"}
                      onChange={(event) => {
                        setSeverity(event.target.value);
                      }}
                      value="intermediate"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Intermediate" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 2, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Intermediate</p>
                    <Radio
                      checked={severity === "severe"}
                      onChange={(event) => {
                        setSeverity(event.target.value);
                      }}
                      value="severe"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Severe" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Severe</p>
                    <Radio
                      checked={severity === "unknown"}
                      onChange={(event) => {
                        setSeverity(event.target.value);
                      }}
                      value="unknown"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Unknown" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 2, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Unknown</p>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="modal-footer"
          >
            <Grid item xs={12} className="confirm-btn-container">
              <div onClick={closeAddAllergyModal} className="cancel-link">
                Cancel
              </div>
              <Button
                onClick={handleSubmit}
                className="confirm-btn"
                style={{ width: 156 }}
              >
                {selectedAllergy ? "Save" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </div>
        <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </MuiModal>
  );
};

export default AddAllergy;
